import { useMemo } from "react";
import { StringLike } from "@visx/scale";

import Select from "ds/components/Select";
import { AspectType } from "types/generated";
import { GroupByKey } from "views/Account/Settings/Usage/Chart/constants";
import UsageChartView from "views/Account/Settings/Usage/Chart/View";
import { getLegendTitleByGroup } from "views/Account/Settings/Usage/Chart/utils";
import CSVDownloadDrawerButton from "views/Account/Settings/Usage/CSVDownload/DrawerButton";
import UsageLayoutHeader from "views/Account/Settings/Usage/Layout/Header";
import UsageLayoutHeaderActions from "views/Account/Settings/Usage/Layout/Header/Actions";

import { getColorMapper } from "../../colorMapper";
import RunMinutesTotalsView from "../../Totals/View";
import { RunMinutesUsageData } from "../types";
import {
  adaptRunMinutesUsageToChart,
  formatRunMinutesUsageDate,
  getRunMinutesUsageChartItems,
} from "../utils";

type UsageRunMinutesNewContentProps = {
  usage: RunMinutesUsageData;
  groupBy: GroupByKey;
  onGroupByChange: (value: string) => void;
};

const GROUP_BY_OPTIONS = [
  { value: GroupByKey.RunType, label: "Run type" },
  { value: GroupByKey.RunState, label: "Run state" },
];

const formatXAxisLabel = (value: StringLike) =>
  formatRunMinutesUsageDate(Number(value), "EEE d MMM");

const UsageRunMinutesNewContent = ({
  usage,
  groupBy,
  onGroupByChange,
}: UsageRunMinutesNewContentProps) => {
  const series = groupBy === GroupByKey.RunState ? usage.byRunState : usage.byRunType;

  const items = useMemo(() => getRunMinutesUsageChartItems(series), [series]);
  const chartData = useMemo(() => adaptRunMinutesUsageToChart(series, items), [series, items]);

  return (
    <>
      <UsageLayoutHeader title="Current period usage chart">
        <UsageLayoutHeaderActions>
          <Select value={groupBy} items={GROUP_BY_OPTIONS} onChange={onGroupByChange} />
          <CSVDownloadDrawerButton aspectType={AspectType.RunMinutes} />
        </UsageLayoutHeaderActions>
      </UsageLayoutHeader>
      <RunMinutesTotalsView
        privateMinutes={usage.privateMinutes}
        publicMinutes={usage.publicMinutes}
      />
      <UsageChartView
        data={chartData}
        items={items}
        colorMapper={getColorMapper(groupBy)}
        formatXAxisLabel={formatXAxisLabel}
        tooltipHeader="Total minutes:"
        legendTitle={getLegendTitleByGroup(groupBy)}
        leftAxisLabel="Minutes"
      />
    </>
  );
};

export default UsageRunMinutesNewContent;
